import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';
import supplierService from '../../../services/supplierService';
import { format } from 'date-fns';

const OrderConversionChart = ({ language, supplierId, startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [conversionRate, setConversionRate] = useState(0);

  const content = {
    en: {
      title: 'Order Conversion Funnel',
      totalInquiries: 'Total Inquiries',
      inProgress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      conversionRate: 'Conversion Rate',
      noData: 'No order data available'
    },
    zh: {
      title: '订单转化漏斗',
      totalInquiries: '总询问数',
      inProgress: '进行中',
      completed: '已完成',
      cancelled: '已取消',
      conversionRate: '转化率',
      noData: '没有订单数据'
    }
  };

  const t = content?.[language];

  const COLORS = {
    pending: 'var(--color-muted)',
    in_progress: 'var(--color-primary)',
    completed: 'var(--color-success)',
    cancelled: 'var(--color-destructive)'
  };

  useEffect(() => {
    loadConversionData();
  }, [supplierId, startDate, endDate]);

  const loadConversionData = async () => {
    try {
      setLoading(true);
      const inquiries = await supplierService?.getSupplierInquiries(
        supplierId,
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd')
      );

      const statusCounts = {
        pending: 0,
        in_progress: 0,
        completed: 0,
        cancelled: 0
      };

      inquiries?.forEach(inquiry => {
        const status = inquiry?.status || 'pending';
        statusCounts[status] = (statusCounts?.[status] || 0) + 1;
      });

      const total = inquiries?.length || 0;
      const completed = statusCounts?.completed || 0;
      const rate = total > 0 ? ((completed / total) * 100)?.toFixed(1) : 0;
      setConversionRate(rate);

      const chartData = [
        { name: t?.totalInquiries, value: statusCounts?.pending, status: 'pending' },
        { name: t?.inProgress, value: statusCounts?.in_progress, status: 'in_progress' },
        { name: t?.completed, value: statusCounts?.completed, status: 'completed' },
        { name: t?.cancelled, value: statusCounts?.cancelled, status: 'cancelled' }
      ]?.filter(item => item?.value > 0);

      setData(chartData);
    } catch (error) {
      console.error('Failed to load conversion data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Filter" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
      </div>

      {data?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      ) : (
        <>
          <div className="text-center mb-4">
            <p className="text-3xl font-bold text-foreground">{conversionRate}%</p>
            <p className="text-sm text-muted-foreground">{t?.conversionRate}</p>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[entry?.status]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};

export default OrderConversionChart;